<template>
  <div>
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h2 class="text-xl font-semibold text-gray-900">データセット一覧</h2>
        <p class="mt-1 text-sm text-gray-500">
          利用可能なデータセットを確認・管理できます
        </p>
      </div>
      <!-- プラットフォーム管理者のみ: 公開データセット作成ボタン -->
      <AppButton
        v-if="authStore.user?.account_type === 'platform_admin'"
        variant="primary"
        @click="showCreateModal = true"
      >
        <svg class="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        公開データセット作成
      </AppButton>
    </div>

    <!-- Loading State -->
    <div v-if="datasetStore.datasetsLoading" class="flex justify-center items-center py-12">
      <div class="flex items-center space-x-2 text-gray-500">
        <svg class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span>読み込み中...</span>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="datasetStore.datasetsError" class="alert-danger">
      <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
      </svg>
      <span>{{ datasetStore.datasetsError }}</span>
    </div>

    <!-- Content -->
    <div v-else>
      <!-- Filters -->
      <div class="mb-6 p-4 bg-white border border-gray-200 rounded-lg">
        <div class="flex flex-wrap gap-4">
          <label class="inline-flex items-center cursor-pointer">
            <input
              type="radio"
              v-model="filter"
              value="all"
              class="form-radio text-primary-600 focus:ring-primary-500"
            />
            <span class="ml-2 text-sm text-gray-700">全て</span>
          </label>
          <label class="inline-flex items-center cursor-pointer">
            <input
              type="radio"
              v-model="filter"
              value="paid"
              class="form-radio text-primary-600 focus:ring-primary-500"
            />
            <span class="ml-2 text-sm text-gray-700">有料のみ</span>
          </label>
          <label class="inline-flex items-center cursor-pointer">
            <input
              type="radio"
              v-model="filter"
              value="free"
              class="form-radio text-primary-600 focus:ring-primary-500"
            />
            <span class="ml-2 text-sm text-gray-700">無料のみ</span>
          </label>
          <label class="inline-flex items-center cursor-pointer">
            <input
              type="radio"
              v-model="filter"
              value="platform"
              class="form-radio text-primary-600 focus:ring-primary-500"
            />
            <span class="ml-2 text-sm text-gray-700">プラットフォーム提供</span>
          </label>
          <label class="inline-flex items-center cursor-pointer">
            <input
              type="radio"
              v-model="filter"
              value="provider"
              class="form-radio text-primary-600 focus:ring-primary-500"
            />
            <span class="ml-2 text-sm text-gray-700">データプロバイダー提供</span>
          </label>
        </div>
      </div>

      <!-- Dataset Grid -->
      <div v-if="filteredDatasets.length > 0" class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <div
          v-for="dataset in filteredDatasets"
          :key="dataset.dataset_id"
          @click="copyDatasetId(dataset.dataset_id)"
          class="card cursor-pointer hover:shadow-lg hover:-translate-y-1 transition-all duration-200 p-6"
        >
          <!-- データセット名 -->
          <h3 class="text-lg font-semibold text-gray-900 mb-3">
            {{ dataset.name }}
          </h3>

          <!-- 説明文 -->
          <p class="text-sm text-gray-500 mb-3 line-clamp-2 min-h-[2.5rem]">
            {{ dataset.description || '説明なし' }}
          </p>

          <!-- データセットID -->
          <div class="mb-4">
            <div class="text-xs text-gray-400 mb-1">データセットID</div>
            <code class="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded border border-gray-200 break-all block">
              {{ dataset.dataset_id }}
            </code>
          </div>

          <!-- 価格とバッジ -->
          <div class="flex justify-between items-center mb-4 pt-3 border-t border-gray-100">
            <div class="text-success-600 font-semibold">
              <span class="text-xl">{{ formatPrice(dataset.price_per_search) }}</span>
              <span class="text-sm"> 円/検索</span>
            </div>
            <span class="badge badge-secondary">
              {{ dataset.is_platform_dataset ? 'プラットフォーム' : 'プロバイダー' }}
            </span>
          </div>

          <!-- 作成日 -->
          <div class="text-xs text-gray-400 border-t border-gray-100 pt-3">
            作成日: {{ formatDate(dataset.created_at) }}
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-12">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-900">データセットがありません</h3>
        <p class="mt-1 text-sm text-gray-500">
          新しいデータセットを作成して始めましょう
        </p>
        <div class="mt-6">
          <AppButton variant="primary" @click="showCreateModal = true">
            <svg class="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            新規データセット作成
          </AppButton>
        </div>
      </div>
    </div>

    <!-- Create Modal -->
    <CreateDatasetModal
      v-if="showCreateModal"
      @close="showCreateModal = false"
      @created="onDatasetCreated"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '../../stores/auth.js'
import { useDatasetStore } from '../../stores/dataset.js'
import AppButton from '../../components/ui/AppButton.vue'
import CreateDatasetModal from '../../components/dataset/CreateDatasetModal.vue'

const router = useRouter()
const toast = useToast()
const authStore = useAuthStore()
const datasetStore = useDatasetStore()

const showCreateModal = ref(false)
const filter = ref('all')

const filteredDatasets = computed(() => {
  switch (filter.value) {
    case 'paid':
      return datasetStore.paidDatasets
    case 'free':
      return datasetStore.freeDatasets
    case 'platform':
      return datasetStore.platformDatasets
    case 'provider':
      return datasetStore.providerDatasets
    default:
      return datasetStore.datasets
  }
})

function formatPrice(price) {
  return parseFloat(price).toFixed(2)
}

function formatDate(dateString) {
  const date = new Date(dateString)
  return date.toLocaleDateString('ja-JP')
}

async function copyDatasetId(datasetId) {
  try {
    await navigator.clipboard.writeText(datasetId)
    toast.success('データセットIDをコピーしました')
  } catch (err) {
    console.error('Failed to copy dataset ID:', err)
    toast.error('データセットIDのコピーに失敗しました')
  }
}

function onDatasetCreated() {
  showCreateModal.value = false
  datasetStore.fetchDatasets()
}

onMounted(() => {
  datasetStore.fetchDatasets()
})
</script>
